import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { format } from 'date-fns';

const EmailLogsGrid = ({ emailLogs, filters, onFilterChange }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [retryingEmails, setRetryingEmails] = useState(new Set());

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-gray-500/20', text: 'text-gray-400', icon: 'Clock', label: 'Pending' },
      sent: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: 'Send', label: 'Sent' },
      delivered: { bg: 'bg-green-500/20', text: 'text-green-400', icon: 'CheckCircle', label: 'Delivered' },
      bounced: { bg: 'bg-orange-500/20', text: 'text-orange-400', icon: 'AlertTriangle', label: 'Bounced' },
      failed: { bg: 'bg-red-500/20', text: 'text-red-400', icon: 'XCircle', label: 'Failed' },
      opened: { bg: 'bg-purple-500/20', text: 'text-purple-400', icon: 'Eye', label: 'Opened' },
      clicked: { bg: 'bg-teal-500/20', text: 'text-[#1FB5A5]', icon: 'MousePointerClick', label: 'Clicked' }
    };
    const badge = badges?.[status] || badges?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        <Icon name={badge?.icon} size={12} />
        {badge?.label}
      </span>
    );
  };

  const getRecipientTypeBadge = (type) => {
    const badges = {
      client: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Client' },
      team: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Team' }
    };
    const badge = badges?.[type] || badges?.client;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge?.bg} ${badge?.text}`}>
        {badge?.label}
      </span>
    );
  };

  const toggleRowExpansion = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const handleRetryEmail = async (emailLog) => {
    if (retryingEmails?.has(emailLog?.id)) return;

    try {
      setRetryingEmails(prev => new Set(prev)?.add(emailLog?.id));
      
      // Add this block - Import emailTrackingService or define placeholder
      // Since emailTrackingService is not imported, we need to handle this
      // For now, commenting out the service calls and showing alert
      alert('Retry functionality requires emailTrackingService to be imported and configured.');
      return;
      
      // Remove or comment out these lines that use undefined emailTrackingService
      /*
      // Check eligibility first
      const isEligible = await emailTrackingService?.isRetryEligible(emailLog?.id);
      
      if (!isEligible) {
        alert('This email is not eligible for retry. It may have reached max retry attempts, be a hard bounce, or have an invalid email address.');
        return;
      }

      // Trigger retry
      const result = await emailTrackingService?.retryEmail(emailLog?.id);
      
      if (result?.success) {
        alert(`Email retry initiated successfully! Retry attempt ${emailLog?.retryCount + 1}`);
      } else {
        alert(`Retry failed: ${result?.error || 'Unknown error'}`);
      }
      */
    } catch (error) {
      console.error('Error retrying email:', error);
      alert(`Failed to retry email: ${error?.message}`);
    } finally {
      setRetryingEmails(prev => {
        const newSet = new Set(prev);
        newSet?.delete(emailLog?.id);
        return newSet;
      });
    }
  };

  const canRetry = (log) => {
    return (
      (log?.status === 'failed' || log?.status === 'bounced') &&
      log?.bounceType !== 'hard' &&
      (!log?.errorCode || !['invalid_email', 'invalid_from_address']?.includes(log?.errorCode))
    );
  };

  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl shadow-elevation-1 p-6 border border-white/20">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-white mb-1">Email Delivery Logs</h3>
          <p className="text-sm text-gray-300">Chronological feed of all booking confirmation emails</p>
        </div>
        <div className="w-10 h-10 bg-[#1FB5A5]/20 rounded-lg flex items-center justify-center">
          <Icon name="List" size={20} className="text-[#1FB5A5]" />
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Status Filter</label>
          <select
            value={filters?.status}
            onChange={(e) => onFilterChange('status', e?.target?.value)}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1FB5A5] backdrop-blur-sm"
          >
            <option value="all" className="bg-[#002B45] text-white">All Status</option>
            <option value="sent" className="bg-[#002B45] text-white">Sent</option>
            <option value="delivered" className="bg-[#002B45] text-white">Delivered</option>
            <option value="bounced" className="bg-[#002B45] text-white">Bounced</option>
            <option value="failed" className="bg-[#002B45] text-white">Failed</option>
            <option value="opened" className="bg-[#002B45] text-white">Opened</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Recipient Type</label>
          <select
            value={filters?.recipientType}
            onChange={(e) => onFilterChange('recipientType', e?.target?.value)}
            className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#1FB5A5] backdrop-blur-sm"
          >
            <option value="all" className="bg-[#002B45] text-white">All Recipients</option>
            <option value="client" className="bg-[#002B45] text-white">Clients</option>
            <option value="team" className="bg-[#002B45] text-white">Team</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">Search Email</label>
          <div className="relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={filters?.searchEmail}
              onChange={(e) => onFilterChange('searchEmail', e?.target?.value)}
              placeholder="Search by email..."
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1FB5A5] backdrop-blur-sm"
            />
          </div>
        </div>
      </div>
      {/* Email Logs Table */}
      <div className="overflow-x-auto">
        {emailLogs?.length > 0 ? (
          <div className="space-y-2">
            {emailLogs?.map((log) => (
              <div key={log?.id} className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
                {/* Main Row */}
                <div 
                  className="p-4 cursor-pointer hover:bg-white/10 transition-colors"
                  onClick={() => toggleRowExpansion(log?.id)}
                >
                  <div className="grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-3">
                      <div className="flex items-center gap-2">
                        <Icon 
                          name={expandedRow === log?.id ? 'ChevronDown' : 'ChevronRight'} 
                          size={16} 
                          className="text-gray-400" 
                        />
                        <div>
                          <p className="text-white font-medium text-sm">{log?.recipientName}</p>
                          <p className="text-gray-400 text-xs">{log?.recipientEmail}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2">
                      {getRecipientTypeBadge(log?.recipientType)}
                    </div>
                    <div className="col-span-2">
                      {getStatusBadge(log?.status)}
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-300 text-xs">
                        {format(new Date(log?.createdAt), 'MMM dd, yyyy')}
                      </p>
                      <p className="text-gray-400 text-xs">
                        {format(new Date(log?.createdAt), 'HH:mm:ss')}
                      </p>
                    </div>
                    <div className="col-span-2">
                      {log?.deliveryDurationMs ? (
                        <p className="text-gray-300 text-xs">
                          {(log?.deliveryDurationMs / 1000)?.toFixed(2)}s
                        </p>
                      ) : (
                        <p className="text-gray-500 text-xs">-</p>
                      )}
                    </div>
                    <div className="col-span-1 text-right">
                      {log?.retryCount > 0 && (
                        <span className="text-yellow-400 text-xs">
                          {log?.retryCount} retries
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRow === log?.id && (
                  <div className="bg-white/5 p-4 border-t border-white/10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-300 mb-2">Email Details</h4>
                        <div className="space-y-2 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Subject:</span>
                            <span className="text-white text-right ml-2">{log?.subject}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">From:</span>
                            <span className="text-white">{log?.fromEmail}</span>
                          </div>
                          {log?.resendEmailId && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">Resend ID:</span>
                              <span className="text-[#1FB5A5] font-mono">{log?.resendEmailId}</span>
                            </div>
                          )}
                          {log?.bookingId && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">Booking ID:</span>
                              <span className="text-white font-mono text-xs">{log?.bookingId}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-gray-300 mb-2">Delivery Timeline</h4>
                        <div className="space-y-2 text-xs">
                          {log?.sentAt && (
                            <div className="flex items-center gap-2">
                              <Icon name="Send" size={12} className="text-blue-400" />
                              <span className="text-gray-400">Sent:</span>
                              <span className="text-white">{format(new Date(log?.sentAt), 'HH:mm:ss')}</span>
                            </div>
                          )}
                          {log?.deliveredAt && (
                            <div className="flex items-center gap-2">
                              <Icon name="CheckCircle" size={12} className="text-green-400" />
                              <span className="text-gray-400">Delivered:</span>
                              <span className="text-white">{format(new Date(log?.deliveredAt), 'HH:mm:ss')}</span>
                            </div>
                          )}
                          {log?.openedAt && (
                            <div className="flex items-center gap-2">
                              <Icon name="Eye" size={12} className="text-purple-400" />
                              <span className="text-gray-400">Opened:</span>
                              <span className="text-white">{format(new Date(log?.openedAt), 'HH:mm:ss')}</span>
                            </div>
                          )}
                          {log?.bouncedAt && (
                            <div className="flex items-center gap-2">
                              <Icon name="AlertTriangle" size={12} className="text-orange-400" />
                              <span className="text-gray-400">Bounced:</span>
                              <span className="text-white">{format(new Date(log?.bouncedAt), 'HH:mm:ss')}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Retry Information */}
                      {(log?.retryCount > 0 || log?.nextRetryAt) && (
                        <div className="md:col-span-2">
                          <h4 className="text-sm font-semibold text-yellow-400 mb-2">Retry Information</h4>
                          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3">
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-400">Retry Attempts:</span>
                                <span className="text-white ml-2 font-semibold">{log?.retryCount}</span>
                              </div>
                              {log?.lastRetryAt && (
                                <div>
                                  <span className="text-gray-400">Last Retry:</span>
                                  <span className="text-white ml-2">{format(new Date(log?.lastRetryAt), 'MMM dd, HH:mm')}</span>
                                </div>
                              )}
                              {log?.nextRetryAt && (
                                <div className="col-span-2">
                                  <span className="text-gray-400">Next Retry Scheduled:</span>
                                  <span className="text-yellow-400 ml-2 font-semibold">{format(new Date(log?.nextRetryAt), 'MMM dd, yyyy HH:mm:ss')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {(log?.errorMessage || log?.bounceType) && (
                        <div className="md:col-span-2">
                          <h4 className="text-sm font-semibold text-red-400 mb-2">Error Information</h4>
                          <div className="bg-red-500/10 border border-red-500/20 rounded p-3">
                            {log?.bounceType && (
                              <p className="text-orange-400 text-xs mb-1">
                                <strong>Bounce Type:</strong> {log?.bounceType}
                              </p>
                            )}
                            {log?.errorMessage && (
                              <p className="text-red-400 text-xs">
                                <strong>Error:</strong> {log?.errorMessage}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Manual Retry Button */}
                      {canRetry(log) && (
                        <div className="md:col-span-2 mt-2">
                          <button
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleRetryEmail(log);
                            }}
                            disabled={retryingEmails?.has(log?.id)}
                            className="w-full px-4 py-2 bg-[#1FB5A5] hover:bg-[#1a9d8f] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                          >
                            {retryingEmails?.has(log?.id) ? (
                              <>
                                <Icon name="Loader2" size={16} className="animate-spin" />
                                Retrying...
                              </>
                            ) : (
                              <>
                                <Icon name="RefreshCw" size={16} />
                                Retry Email Now
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Inbox" size={48} className="text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No email logs found</p>
            <p className="text-gray-500 text-sm mt-2">Emails will appear here as they are sent</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailLogsGrid;